import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTimeIST } from '../utils/dateUtils';
import { Activity, Monitor, Trash2, ChevronDown, ChevronRight } from 'lucide-react';

const formatDuration = (seconds) => {
  const s = Math.round(seconds || 0);
  if (s < 60) return `${s}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const formatTime = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true
  });
};

const eventLabels = {
  login: 'Logged in',
  work: 'Working',
  idle: 'Went idle',
  away: 'Left the tab / app',
  closed: 'Closed / logged out'
};

const eventColors = {
  login: 'bg-blue-100 text-blue-700',
  work: 'bg-green-100 text-green-700',
  idle: 'bg-yellow-100 text-yellow-700',
  away: 'bg-orange-100 text-orange-700',
  closed: 'bg-gray-200 text-gray-700'
};

const SessionsPage = () => {
  const { api } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);

  const fetchSessions = useCallback(async () => {
    try {
      setError('');
      const res = await api.get('/auth/sessions');
      setSessions(res.data.sessions || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load activity log');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchSessions();
    const interval = setInterval(fetchSessions, 60000);
    return () => clearInterval(interval);
  }, [fetchSessions]);

  const clearLogs = async () => {
    if (!window.confirm('Delete ALL activity logs? This cannot be undone.')) return;
    try {
      await api.delete('/auth/sessions');
      setSessions([]);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to clear logs');
    }
  };

  const statusBadge = (session) => {
    const map = {
      closed: 'bg-gray-100 text-gray-700',
      idle: 'bg-yellow-100 text-yellow-700',
      away: 'bg-orange-100 text-orange-700',
      active: 'bg-green-100 text-green-700'
    };
    const label = { closed: 'Closed', idle: 'Idle', away: 'Away', active: 'Active' };
    const status = session.status || 'active';
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${map[status] || map.active}`}>
        {label[status] || status}
      </span>
    );
  };

  const renderTimeline = (session) => {
    const events = session.events || [];
    if (events.length === 0) {
      return <p className="text-sm text-gray-500 py-3">No timeline recorded for this session</p>;
    }
    return (
      <div className="py-3">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-1 pr-6 font-medium">Time</th>
              <th className="py-1 pr-6 font-medium">Event</th>
              <th className="py-1 font-medium">Duration in this state</th>
            </tr>
          </thead>
          <tbody>
            {events.map((ev, i) => {
              const next = events[i + 1];
              const end = next ? new Date(next.at) : (session.status === 'closed' && session.logoutAt ? new Date(session.logoutAt) : new Date());
              const secs = Math.max(0, (end - new Date(ev.at)) / 1000);
              return (
                <tr key={i} className="border-t border-gray-100">
                  <td className="py-1.5 pr-6 text-gray-600">{formatTime(ev.at)}</td>
                  <td className="py-1.5 pr-6">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${eventColors[ev.type] || 'bg-gray-100 text-gray-700'}`}>
                      {eventLabels[ev.type] || ev.type}
                    </span>
                  </td>
                  <td className="py-1.5 text-gray-600">{next || session.status === 'closed' ? formatDuration(secs) : 'ongoing'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center">
            <Activity className="h-5 w-5 mr-2" />
            User Activity Log
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Login sessions, device, work and idle time for each user. Click a row for the full timeline.
          </p>
        </div>
        <button
          onClick={clearLogs}
          className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700 flex items-center"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Clear Logs
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 m-4 rounded-lg">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-center py-8 text-gray-500">Loading...</p>
      ) : sessions.length === 0 ? (
        <p className="text-center py-8 text-gray-500">No activity recorded yet</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="py-3 px-4 w-8"></th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Device</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">IP</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Login</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Last Active</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Worked</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Idle</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Away</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Closed At</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sessions.map((s) => (
                <React.Fragment key={s._id}>
                  <tr
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setExpanded(expanded === s._id ? null : s._id)}
                  >
                    <td className="py-3 px-4 text-gray-400">
                      {expanded === s._id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </td>
                    <td className="py-3 px-4">
                      <p className="font-medium text-gray-900">{s.userName || 'Unknown'}</p>
                      <p className="text-xs text-gray-500">{(s.userRole || '').replace('_', ' ')}</p>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      <div className="flex items-center">
                        <Monitor className="h-4 w-4 mr-2 text-gray-400" />
                        {s.device || 'Unknown'}
                      </div>
                      {s.deviceId && (
                        <p className="text-xs text-gray-400 font-mono mt-0.5" title={s.deviceId}>
                          ID: {s.deviceId.slice(0, 8)}
                        </p>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 font-mono">{s.ipAddress || '-'}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{formatDateTimeIST(s.loginAt)}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{formatDateTimeIST(s.lastActiveAt)}</td>
                    <td className="py-3 px-4 text-sm text-green-700">{formatDuration(s.activeSeconds)}</td>
                    <td className="py-3 px-4 text-sm text-yellow-700">{formatDuration(s.idleSeconds)}</td>
                    <td className="py-3 px-4 text-sm text-orange-700">{formatDuration(s.hiddenSeconds)}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {s.logoutAt ? formatDateTimeIST(s.logoutAt) : '-'}
                    </td>
                    <td className="py-3 px-4">{statusBadge(s)}</td>
                  </tr>
                  {expanded === s._id && (
                    <tr>
                      <td colSpan="11" className="bg-gray-50 px-8">
                        {renderTimeline(s)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SessionsPage;
